/* eslint-disable @typescript-eslint/no-unsafe-type-assertion */
// oxlint-disable typescript/no-explicit-any
import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { LOGS } from '@kp/env';
import { BROWSER, toArray } from '@kp/utils';
import { getUser } from '@kp/workers';
import { from, of, switchMap } from 'rxjs';

import type { OnDestroy } from '@angular/core';

const LogLevel = [
  'OFF',
  'FATAL',
  'ERROR',
  'WARN',
  'INFO',
  'DEBUG',
  'TRACE',
] as const;
type ConsoleMethod = (...args: Array<any>) => void;

type LogLevel = (typeof LogLevel)[number];

// Marker to prevent double patching
const PATCH_MARK = Symbol('logServicePatched');

const replacer = (_key: string, value: any): any => {
  if (value instanceof Error) {
    const base: Record<string, unknown> = {
      message: value.message,
      name: value.name,
      stack: value.stack,
    };
    for (const k of Object.getOwnPropertyNames(value)) {
      (base as any)[k] = (value as any)[k];
    }

    return base;
  }

  return value;
};

@Service()
export class LogService implements OnDestroy {
  readonly #browser = inject(BROWSER);
  readonly #http = inject(HttpClient);

  // Original console method references
  private readonly original = {
    debug: console.debug as ConsoleMethod,
    error: console.error as ConsoleMethod,
    log: console.log as ConsoleMethod,
    warn: console.warn as ConsoleMethod,
  };

  constructor() {
    this.patchConsole();
  }

  debug(msg: any): void {
    console.debug(...toArray(msg));
  }

  // ----------------------------
  // Public API (unchanged)
  // ----------------------------
  error(error: any): void {
    console.error(...toArray(error));
  }

  log(msg: any): void {
    console.log(...toArray(msg));
  }

  private patchConsole(): void {
    if (
      (console.error as any)[PATCH_MARK] ||
      (console.log as any)[PATCH_MARK] ||
      (console.warn as any)[PATCH_MARK] ||
      (console.debug as any)[PATCH_MARK]
    ) {
      return;
    }

    // ERROR: always print + log to backend
    this.patchMethod('error', 'ERROR', { printInProd: true });

    // INFO (console.log)
    this.patchMethod('log', 'INFO', { printInProd: false });

    // WARN
    this.patchMethod('warn', 'WARN', { printInProd: false });

    // DEBUG
    this.patchMethod('debug', 'DEBUG', { printInProd: false });
  }

  private patchMethod(
    method: 'debug' | 'error' | 'log' | 'warn',
    level: Extract<LogLevel, 'DEBUG' | 'ERROR' | 'INFO' | 'WARN'>,
    opts: { printInProd: boolean },
  ): void {
    const original = this.original[method];

    const patched: ConsoleMethod = (...args: Array<any>) => {
      if (!__PRODUCTION && method !== 'error') {
        // In dev: keep original behavior AND preserve original call site
        (console as any)[method] = original.bind(console);

        return;
      }

      // Print rules:
      // - in DEV → always print
      // - in PROD → only print ERROR
      if (!__PRODUCTION || opts.printInProd) {
        Reflect.apply(original, console, args);
      }

      // Backend logging (async to avoid messing with stack frames)
      if (__PRODUCTION) {
        queueMicrotask(() => {
          try {
            const message = this.stringifyArgsForTransport(args);
            this.save(message, level);
          } catch {
            /* logging must not break anything */
          }
        });
      }
    };

    // Mask wrapper as native-like function
    try {
      Object.defineProperty(patched, 'name', { value: original.name });
      Object.defineProperty(patched, 'length', { value: original.length });
      Object.defineProperty(patched, 'toString', {
        value: () => `function ${method}() { [native code] }`,
      });
      (patched as any)[PATCH_MARK] = true;
    } catch {
      /* not critical */
    }

    (console as any)[method] = patched;
  }

  // ----------------------------
  // Monkey patching
  // ----------------------------
  /** Restore original console functions */
  restoreConsole(): void {
    console.error = this.original.error;
    console.log = this.original.log;
    console.warn = this.original.warn;
    console.debug = this.original.debug;
  }

  save(message: string, standardLevel: LogLevel): void {
    if (
      __APP_NAME === 'TIIA' &&
      typeof message === 'string' &&
      (message.includes('Site24x7') || message.includes('AdsBot-Google-Mobile'))
    ) {
      return;
    }

    from(__APP_NAME === 'TIIA' ? of(undefined) : getUser())
      .pipe(
        switchMap(user =>
          this.#http.post(`/${LOGS}/eventLogger/log/${__APP_NAME}NG`, {
            appId: `${__APP_NAME}NG`,
            level: { standardLevel },
            message: {
              browser: this.#browser,
              message,
              url: typeof location === 'undefined' ? undefined : location.href,
              ...(user ? { user: user.userName } : {}),
            },
          }),
        ),
      )
      // eslint-disable-next-line @smarttools/rxjs/no-ignored-subscribe
      .subscribe();
  }

  private stringifyArgsForTransport(args: Array<any>): string {
    try {
      if (args.length === 1) {
        // oxlint-disable-next-line prefer-destructuring
        const v = args[0];
        if (v && typeof v === 'object') {
          return JSON.stringify(v, Object.getOwnPropertyNames(v));
        }
      }

      return JSON.stringify(args, replacer);
    } catch {
      return args
        .map(x => {
          try {
            if (typeof x === 'string') {
              return x;
            }
            if (x instanceof Error) {
              return `${x.name}: ${x.message}`;
            }

            return JSON.stringify(x);
          } catch {
            return String(x);
          }
        })
        .join(' ');
    }
  }

  // ----------------------------
  // Serialization & save()
  // ----------------------------
  warn(msg: any): void {
    console.warn(...toArray(msg));
  }

  ngOnDestroy(): void {
    this.restoreConsole();
  }
}
